import { Spin } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { claimWorldcupReward, getWorldcupHistoryPredictionsByAccount } from 'service/base';

const titleDecor = [
  { side: 'left', key: 'left-1' },
  { side: 'left', key: 'left-2' },
  { side: 'right', key: 'right-1' },
  { side: 'right', key: 'right-2' }
];

const getRecordValue = (record, keys, fallback = null) => {
  if (!record || !Array.isArray(keys)) return fallback;

  for (let index = 0; index < keys.length; index += 1) {
    const key = keys[index];
    const value = record?.[key];
    if (value === 0) return 0;
    if (value !== undefined && value !== null && `${value}`.trim() !== '') {
      return value;
    }
  }

  return fallback;
};

const buildHistoryRows = (items) => {
  if (!Array.isArray(items) || items.length === 0) {
    return [];
  }

  return items
    .map((item, idx) => {
      const createdAt = dayjs(item?.createdAt);
      const rawId = item?.id ?? item?._id ?? null;
      return {
        id: rawId
          ? `${rawId}`
          : `${item?.type || ''}-${item?.name || ''}-${item?.createdAt || idx}-${idx}`,
        rewardId: rawId ? `${rawId}` : '',
        account: item?.account || '',
        predictType: item?.predictType || '',
        timestamp: createdAt.isValid() ? createdAt.valueOf() : 0,
        time: createdAt.isValid() ? createdAt.format('DD/MM/YYYY') : '--',
        predicted: item?.type || '-',
        userPrediction: item?.name || '-',
        result: getRecordValue(item, ['result', 'isWin', 'winResult', 'match_result'], null),
        point: getRecordValue(item, [
          'point',
          'points',
          'rewardPoint',
          'rewardPoints',
          'bonusPoint',
          'bonusPoints'
        ], 0),
        rewardStatus: getRecordValue(
          item,
          [
            'status_register',
            'statusRegister',
            'register_status',
            'received',
            'isReceived',
            'claimed',
            'isClaimed',
            'isReceiveReward',
            'isClaimedReward'
          ],
          null
        )
      };
    })
    .sort((a, b) => b.timestamp - a.timestamp);
};

const PredictionHistoryTable = ({
  account = '',
  site = 'qq88',
  title = 'LỊCH SỬ',
  activeHistoryTab = 'worldcup',
  onScoreTabClick,
  onWorldCupTabClick
}) => {
  const [historyData, setHistoryData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [claimingId, setClaimingId] = useState('');

  useEffect(() => {
    let isCancelled = false;

    const timer = setTimeout(async () => {
      try {
        setIsLoading(true);
        const res = await getWorldcupHistoryPredictionsByAccount();

        if (isCancelled) return;
        const nextData = Array.isArray(res?.data) ? res.data : [];
        setHistoryData(nextData);
      } catch (error) {
        if (isCancelled) return;
        setHistoryData([]);
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    }, 500);

    return () => {
      isCancelled = true;
      clearTimeout(timer);
    };
  }, [account, site]);

  const rows = useMemo(() => buildHistoryRows(historyData), [historyData]);

  return (
    <section className="mx-auto mt-4 w-full max-w-[1236px]">
      <div className="rounded-[24px] border-[3px] border-[#006719] bg-[#d7e6da] p-3 md:p-5">
        <div className="mx-auto mb-3 flex w-fit items-center justify-center gap-2 md:mb-4">
          {titleDecor
            .filter((item) => item.side === 'left')
            .map((item) => (
              <span
                key={item.key}
                className="h-7 w-[6px] skew-x-[-12deg] rounded-full bg-gradient-to-b from-[#17d147] to-[#0e7e2c] md:h-8"
              />
            ))}

          <div className="h-[34px] min-w-[210px] rounded-[8px] border border-[#00a130] bg-gradient-to-b from-[#00c838] to-[#008f28] px-5 text-center text-[22px] font-bold uppercase leading-[32px] text-[#f5fffb] md:min-w-[250px] md:px-8">
            {title}
          </div>

          {titleDecor
            .filter((item) => item.side === 'right')
            .map((item) => (
              <span
                key={item.key}
                className="h-7 w-[6px] skew-x-[-12deg] rounded-full bg-gradient-to-b from-[#17d147] to-[#0e7e2c] md:h-8"
              />
            ))}
        </div>

        <div className="mb-3 md:mb-4 flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => onScoreTabClick?.()}
            className={`h-[32px] md:h-[38px] min-w-[128px] md:min-w-[180px] rounded-[8px] border-none appearance-none px-4 text-[13px] md:text-[20px] font-bold text-white outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 ${
              activeHistoryTab === 'score'
                ? 'bg-gradient-to-b from-[#00CD32] to-[#048724]'
                : 'bg-[#9ea3a7]'
            }`}
          >
            Dự đoán tỉ số
          </button>

          <div
            className={`rounded-[9px] p-[1px] md:rounded-[11px] md:p-[2px] ${
              activeHistoryTab === 'worldcup'
                ? 'bg-gradient-to-b from-[#006719] to-[#2CF65D]'
                : 'bg-[#9ea3a7]'
            }`}
          >
            <button
              type="button"
              onClick={() => onWorldCupTabClick?.()}
              className={`h-[32px] md:h-[38px] min-w-[128px] md:min-w-[180px] rounded-[8px] border-none appearance-none px-4 text-[13px] md:text-[20px] font-bold text-white outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 ${
                activeHistoryTab === 'worldcup'
                  ? 'bg-gradient-to-b from-[#00CD32] to-[#048724]'
                  : 'bg-[#9ea3a7]'
              }`}
            >
              Dự đoán World Cup
            </button>
          </div>
        </div>

        <div className="overflow-x-auto rounded-[10px] border border-[#8abc97]">
          <table className="w-full border-separate border-spacing-0 text-center text-[11px] text-[#0f6b2c] md:text-[14px]">
            <thead>
              <tr className="bg-[#006a1f] text-[#f4e251]">
                <th className="border-r border-b border-[#8abc97] px-2 py-2 text-[12px] font-semibold uppercase md:text-[24px]">
                  Ngày dự đoán
                </th>
                <th className="border-r border-b border-[#8abc97] px-2 py-2 text-[12px] font-semibold uppercase md:text-[24px]">
                  DỰ ĐOÁN
                </th>
                <th className="border-r border-b border-[#8abc97] px-2 py-2 text-[12px] font-semibold uppercase md:text-[24px]">
                  BẠN DỰ ĐOÁN
                </th>
                <th className="border-b border-[#8abc97] px-2 py-2 text-[12px] font-semibold uppercase md:text-[24px]">
                  KẾT QUẢ
                </th>
                <th className="border-b border-[#8abc97] px-2 py-2 text-[12px] font-semibold uppercase md:text-[24px]">
                  NHẬN THƯỞNG
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr className="bg-[#dce3df]">
                  <td className="border-b border-[#8abc97] px-2 py-4" colSpan={5}>
                    <Spin size="small" />
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr className="bg-[#dce3df]">
                  <td className="border-r border-b border-[#8abc97] px-2 py-[6px] md:text-[15px]">-</td>
                  <td className="border-r border-b border-[#8abc97] px-2 py-[6px] md:text-[15px]">-</td>
                  <td className="border-r border-b border-[#8abc97] px-2 py-[6px] md:text-[15px]">-</td>
                  <td className="border-b border-[#8abc97] px-2 py-[6px] md:text-[15px]">-</td>
                  <td className="border-b border-[#8abc97] px-2 py-[6px] md:text-[15px]">-</td>
                </tr>
              ) : (
                rows.map((row) => {
                  const normalizedResult =
                    typeof row.result === 'string' ? row.result.trim().toLowerCase() : row.result;
                  const isWon =
                    normalizedResult === true ||
                    normalizedResult === 1 ||
                    normalizedResult === '1' ||
                    normalizedResult === 'true' ||
                    normalizedResult === 'win' ||
                    normalizedResult === 'correct';
                  const numericPoint = Number(row.point) || 0;
                  const pointDisplay =
                    isWon && numericPoint > 0 ? Intl.NumberFormat('vi-VN').format(numericPoint) : '-';

                  const normalizedRewardStatus =
                    typeof row.rewardStatus === 'string'
                      ? row.rewardStatus.trim().toLowerCase()
                      : row.rewardStatus;
                  const isAccepted =
                    normalizedRewardStatus === 'accept' ||
                    normalizedRewardStatus === true ||
                    normalizedRewardStatus === 1 ||
                    normalizedRewardStatus === '1';
                  const isApply = normalizedRewardStatus === 'apply';
                  const canClaim =
                    isWon && isApply && Boolean(row.rewardId) && Boolean(row.predictType);
                  const isClaiming = claimingId === row.id;

                  return (
                    <tr key={row.id} className="bg-[#dce3df]">
                      <td className="border-r border-b border-[#8abc97] px-2 py-[6px] text-[11px] md:text-[15px]">
                        {row.time}
                      </td>
                      <td className="border-r border-b border-[#8abc97] px-2 py-[6px] md:text-[15px]">
                        {row.predicted}
                      </td>
                      <td className="border-r border-b border-[#8abc97] px-2 py-[6px] md:text-[15px]">
                        {row.userPrediction}
                      </td>
                      <td className="border-r border-b border-[#8abc97] px-2 py-[6px] md:text-[15px]">
                        {pointDisplay}
                      </td>
                      <td className="border-b border-[#8abc97] px-2 py-[6px] md:text-[15px]">
                        <button
                          type="button"
                          disabled={!canClaim || isClaiming}
                          onClick={async () => {
                            if (!canClaim || isClaiming) return;
                            try {
                              setClaimingId(row.id);
                              const payload = {
                                id: row.rewardId,
                                account: account || row.account || '',
                                predictType: row.predictType
                              };
                              const res = await claimWorldcupReward(payload);
                              if (res?.code === 0) {
                                setHistoryData((prev) =>
                                  (Array.isArray(prev) ? prev : []).map((item) => {
                                    const itemId = `${item?.id ?? item?._id ?? ''}`;
                                    if (itemId !== row.rewardId) return item;
                                    return {
                                      ...item,
                                      status_register: 'accept'
                                    };
                                  })
                                );
                              }
                            } catch (error) {
                              // Keep silent here; table state remains unchanged on failure.
                            } finally {
                              setClaimingId('');
                            }
                          }}
                          className={`mx-auto block h-[30px] md:h-[34px] w-full max-w-[96px] md:max-w-[112px] rounded-[10px] px-2 text-[10px] md:text-[13px] font-semibold leading-[30px] md:leading-[34px] text-white border-0 ${
                            canClaim
                              ? 'cursor-pointer bg-gradient-to-b from-[#00CD32] to-[#048724]'
                              : 'cursor-not-allowed bg-[#9ea3a7]'
                          }`}
                        >
                          {isClaiming ? 'Đang nhận...' : isAccepted ? 'Đã nhận thưởng' : 'Nhận thưởng'}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export default PredictionHistoryTable;
